import { Button } from "@/components/ui/button";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Controller, useFormContext } from "react-hook-form";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { ChevronDown } from "lucide-react";
import { useState, useEffect } from "react";
import { sortShiftArray } from "@/utils/nurse-util";

interface ShiftPopoverProps {
  shiftOptions: Array<{ label: string; value: string }>;
  fixedShiftOptions?: Array<{ label: string; value: string }>;
  disabled?: boolean;
}

export function ShiftPopover({
  shiftOptions,
  fixedShiftOptions = [],
  disabled,
}: ShiftPopoverProps) {
  const { control, watch, setValue } = useFormContext();
  const selectedShifts = watch("allowed_shifts") as string[] | undefined;
  const fixedShift = watch("fixed_shift") as string | null | undefined;

  // Popover가 열릴 때마다 올바른 탭으로 초기화
  const [activeTab, setActiveTab] = useState<string>(() => {
    return fixedShift ? "fixed" : "shift";
  });

  // 값이 변경될 때마다 탭 업데이트
  useEffect(() => {
    const newTab = fixedShift ? "fixed" : "shift";
    setActiveTab(newTab);
  }, [fixedShift]);

  // 표시값 계산 (고정근무도 D/E/N/M 코드로 통일)
  // allowed_shifts는 포함 목록: [] = 모두 허용, 값이 있으면 해당 코드만 허용 (근무표 생성·읽기 전용 표시와 동일)
  const getDisplayValue = () => {
    if (fixedShift) {
      const fixedOption = fixedShiftOptions.find((o) => o.value === fixedShift);
      return fixedOption?.value ?? fixedShift;
    }

    if (!selectedShifts || selectedShifts.length === 0) {
      const allLabels = shiftOptions.map((o) => o.label).join(", ");
      return allLabels;
    }

    const labels = selectedShifts
      .map((code) => shiftOptions.find((o) => o.value === code)?.label)
      .filter((label): label is string => Boolean(label));
    return labels.join(", ") || "-";
  };

  const displayValue = getDisplayValue();

  const allShiftCodes = shiftOptions.map((o) => o.value);

  const getAllowedCodes = (currentValue: string[]): Set<string> => {
    if (!currentValue || currentValue.length === 0) {
      return new Set(allShiftCodes);
    }
    return new Set(currentValue);
  };

  const handleCheckboxChange = (
    currentValue: string[],
    shift: string,
    checked: boolean,
    onChange: (val: string[]) => void
  ) => {
    setValue("fixed_shift", null);

    const allowed = getAllowedCodes(currentValue);

    if (checked) {
      allowed.add(shift);
      const isAllSelected =
        allowed.size === allShiftCodes.length &&
        allShiftCodes.every((c) => allowed.has(c));
      if (isAllSelected) {
        onChange([]);
      } else {
        onChange(sortShiftArray([...allowed]));
      }
    } else {
      allowed.delete(shift);
      if (allowed.size === 0) {
        onChange([]);
      } else {
        onChange(sortShiftArray([...allowed]));
      }
    }
  };

  const handleFixedShiftClick = (
    shift: string,
    isSelected: boolean,
    onChange: (val: string | null) => void
  ) => {
    if (disabled) return;

    if (isSelected) {
      onChange(null);
    } else {
      setValue("allowed_shifts", []);
      setValue("is_weekend_off", true);
      onChange(shift);
    }
  };

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          className="!min-w-[60px] !h-[30px] !px-2 flex justify-between items-center gap-1 bg-white"
          disabled={disabled}
        >
          <span className="truncate">{displayValue}</span>
          <ChevronDown className="size-3.5 opacity-50 shrink-0" />
        </Button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-auto p-0!">
        <Tabs
          value={activeTab}
          onValueChange={(val) => {
            setActiveTab(val);
          }}
          className="h-auto! p-0! rounded-none! border-0 shadow-none! gap-0!"
        >
          <TabsList className="rounded-none! p-0! grid grid-cols-2 w-full border-0 shadow-none!">
            <TabsTrigger
              className="text-sm! shadow-none! rounded-none border-0"
              value="shift"
            >
              근무코드
            </TabsTrigger>
            <TabsTrigger
              className="text-sm! shadow-none! rounded-none border-0"
              value="fixed"
            >
              고정근무
            </TabsTrigger>
          </TabsList>
          <TabsContent value="shift" className="p-0! m-0!">
            <Controller
              control={control}
              name="allowed_shifts"
              render={({ field: { value, onChange } }) => {
                const currentValue = Array.isArray(value) ? value : [];
                return (
                  <div className="flex flex-col p-0!">
                    {shiftOptions.map((option) => {
                      const allowed =
                        currentValue.length === 0
                          ? new Set(shiftOptions.map((o) => o.value))
                          : new Set(currentValue);
                      const isSelected = allowed.has(option.value);
                      return (
                        <div
                          key={option.value}
                          className={`flex items-center rounded-none! gap-2 px-2 py-1.5 transition-colors
                            ${isSelected ? "bg-blue-100" : "hover:bg-accent"}
                          `}
                        >
                          <Checkbox
                            id={`shift-${option.value}`}
                            checked={isSelected}
                            onCheckedChange={(checked) =>
                              handleCheckboxChange(
                                currentValue,
                                option.value,
                                !!checked,
                                onChange
                              )
                            }
                            disabled={disabled}
                            className="data-[state=checked]:bg-blue-500 data-[state=checked]:border-blue-500"
                          />
                          <Label
                            htmlFor={`shift-${option.value}`}
                            className="text-sm cursor-pointer flex-1"
                          >
                            {option.label}
                          </Label>
                        </div>
                      );
                    })}
                  </div>
                );
              }}
            />
          </TabsContent>
          <TabsContent value="fixed" className="p-0! m-0!">
            <Controller
              control={control}
              name="fixed_shift"
              render={({ field }) => {
                return (
                  <div className="flex flex-col">
                    {fixedShiftOptions.length > 0 ? (
                      fixedShiftOptions.map((option) => {
                        const isSelected = field.value === option.value;
                        return (
                          <div
                            key={option.value}
                            onClick={() =>
                              handleFixedShiftClick(
                                option.value,
                                isSelected,
                                field.onChange
                              )
                            }
                            className={`flex items-center rounded-none! px-4 py-2 transition-colors text-sm
                              ${isSelected ? "bg-blue-100 text-blue-800 font-medium" : "hover:bg-accent"}
                              ${disabled ? "opacity-50 cursor-not-allowed" : "cursor-pointer"}
                            `}
                          >
                            {option.label}
                          </div>
                        );
                      })
                    ) : (
                      <div className="px-4 py-2 text-sm text-gray-400">
                        고정근무 옵션이 없습니다
                      </div>
                    )}
                  </div>
                );
              }}
            />
          </TabsContent>
        </Tabs>
      </PopoverContent>
    </Popover>
  );
}
